import {
  ACTIVITIES_COMPONENT_TYPE_AUDIO,
  ACTIVITIES_COMPONENT_TYPE_DOCUMENT,
  ACTIVITIES_COMPONENT_TYPE_EBOOK,
  ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH,
  ACTIVITIES_COMPONENT_TYPE_IMAGE,
  ACTIVITIES_COMPONENT_TYPE_INFO_BOX,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_LINK,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_SLIDESHOW,
  ACTIVITIES_COMPONENT_TYPE_VIDEO,
  ACTIVITIES_COMPONENT_TYPE_WEBTOOL,
  ACTIVITIES_COMPONENT_TYPE_VOCABULARY,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_DRAW,
  ACTIVITIES_COMPONENT_TYPE_TABLE,
  ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE,
  ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION,
  ACTIVITIES_COMPONENT_TYPE_GIF,
  ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE,
  LESSON_ACTIVITIES,
} from 'global/constants';
import {
  ComponentAudio,
  ComponentGuidedResearch,
  ComponentImage,
  ComponentInfoBox,
  ComponentLink,
  ComponentResponse,
  ComponentVideo,
  ComponentEquationResponse,
  ComponentSlideshow,
  ComponentMultipleChoice,
  ComponentEbook,
  ComponentDocument,
  ComponentInteractive,
  ComponentEbookInteractive,
  ComponentVocabulary,
  ComponentFillInBlanks,
  ComponentDraw,
  ComponentTable,
  ComponentDiscussion,
  ComponentInteractiveResponseSequence,
  ComponentInteractiveResponseCategorise,
  ComponentRiveAnimation,
  ComponentGif,
  ComponentImageSphere,
} from 'approot/activity-builder/components/component.constants';
import InfoBoxView from 'approot/activity-builder/components/info-box/info-box-view';
import VideoView from 'approot/activity-builder/components/video/video-view';
import ImageView from 'approot/activity-builder/components/image/image-view';
import ImageSphereView from 'approot/activity-builder/components/image-sphere/image-sphere-view';
import LinkView from 'approot/activity-builder/components/link/link-view';
import WebToolView from 'approot/activity-builder/components/webtool/webtool-view';
import GuidedResearchView from 'approot/activity-builder/components/guided-research/guided-research-view';
import EquationResponseView from 'approot/activity-builder/components/equation-response/equation-response-view';
import SlideshowView from 'approot/activity-builder/components/slideshow/slideshow-view';
import MultipleChoiceStudentAssessment from 'approot/activity-builder/components/multiple-choice/multiple-choice-student-assessment';
import MultipleChoiceStudentLesson from 'approot/activity-builder/components/multiple-choice/multiple-choice-student-lesson';
import EbookView from 'approot/activity-builder/components/ebook/ebook-view';
import DocumentView from 'approot/activity-builder/components/document/document-view';
import InteractiveView from 'approot/activity-builder/components/interactive/interactive-view';
import InteractiveEbookView from 'approot/activity-builder/components/interactive-ebook/interactive-ebook-view';
import VocabularyView from 'approot/activity-builder/components/vocabulary/vocabulary-view';
import RiveView from 'approot/activity-builder/components/rive/rive-view';
import GifView from 'approot/activity-builder/components/gif/gif-view';
import ResponseStudent from 'approot/activity-builder/components/response/student/response-student';
import ResponseStudentStatic from 'approot/activity-builder/components/response/student/response-student-static';
import TableStudent from 'approot/activity-builder/components/table/table-student';
import DrawStudent from 'approot/activity-builder/components/draw/draw-student';
import InteractiveResponseStudent from 'approot/activity-builder/components/interactive-response/sequence/interactive-response-student';
import DiscussionStudent from 'approot/activity-builder/components/discussion/discussion-student';
import FillInBlanksStudent from 'approot/activity-builder/components/fill-in-blanks/student/fill-in-blanks-student';
import {
  ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
  ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
  ComponentEventData,
  ComponentExpandModeType,
  pushComponentViewEvent,
} from '../../present/activity-builder-present-data-tracking.utils';
import { EVENT_ACTION_EDIT } from 'approot/shared/data-tracker/data-events.constants';
import type { InteractiveLessonSectionComponentFragment } from '../../lesson-builder.graphql.generated';
import InteractiveResponseCategoriseStudent from 'approot/activity-builder/components/interactive-response/categorise/interactive-response-categorise-student';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';

type Props = {
  component: InteractiveLessonSectionComponentFragment;
  eventTrackingData?: ComponentEventData;
  contentId: number;
  isLoggedIn: boolean;
  handleOpenModal: (
    component: InteractiveLessonSectionComponentFragment,
    expandMode?: ComponentExpandModeType
  ) => void;
};

const StudentComponents = ({
  component,
  contentId,
  eventTrackingData,
  isLoggedIn,
  handleOpenModal,
}: Props) => {
  const openModalProps = { openModal: () => handleOpenModal(component) };

  const { contentType } = useGetInteractiveLessonParams();
  const isLesson = contentType === LESSON_ACTIVITIES;
  return (
    <>
      {component.type === ACTIVITIES_COMPONENT_TYPE_INFO_BOX && (
        <InfoBoxView isStudent {...(component as ComponentInfoBox)} />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_SLIDESHOW && (
        <SlideshowView
          isStudent
          {...(component as ComponentSlideshow)}
          onComponentExpand={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {isLoggedIn &&
        component.type === ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE && (
          <ResponseStudent {...(component as ComponentResponse)} />
        )}

      {!isLoggedIn &&
        component.type === ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE && (
          <ResponseStudentStatic
            {...(component as ComponentResponse)}
            openModal={() => handleOpenModal(component)}
          />
        )}

      {component.type ===
        ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE && (
        <EquationResponseView {...(component as ComponentEquationResponse)} />
      )}

      {component.type ===
        ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE &&
        (isLesson ? (
          <MultipleChoiceStudentLesson
            {...(component as ComponentMultipleChoice)}
            isLoggedIn={isLoggedIn}
          />
        ) : (
          <MultipleChoiceStudentAssessment
            {...(component as ComponentMultipleChoice)}
            isLoggedIn={isLoggedIn}
          />
        ))}

      {component.type === ACTIVITIES_COMPONENT_TYPE_TABLE && (
        <TableStudent
          {...(component as ComponentTable)}
          isLoggedIn={isLoggedIn}
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK && (
        <EbookView
          isStudent
          {...(component as ComponentEbook)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_DOCUMENT && (
        <DocumentView
          {...(component as ComponentDocument)}
          isStudent
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_VIDEO && (
        <VideoView
          {...(component as ComponentVideo)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_AUDIO && (
        <VideoView
          {...(component as ComponentAudio)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE && (
        <ImageView
          {...(component as ComponentImage)}
          openModal={() => handleOpenModal(component)}
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE && (
        <ImageSphereView {...(component as ComponentImageSphere)} />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE && (
        <InteractiveView
          {...(component as ComponentInteractive)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE && (
        <InteractiveEbookView
          {...(component as ComponentEbookInteractive)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_LINK && (
        <LinkView
          {...(component as ComponentLink)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_WEBTOOL && (
        <WebToolView
          {...(component as ComponentLink)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH && (
        <GuidedResearchView
          {...(component as ComponentGuidedResearch)}
          onStimulusOpen={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_VOCABULARY && (
        <VocabularyView
          isStudent
          {...(component as ComponentVocabulary)}
          onComponentExpand={({ vocabularyWord, vocabularyWordCount }) =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
              eventTrackingData,
              contentId,
              vocabularyWord,
              vocabularyWordCount,
            })
          }
        />
      )}

      {component.type ===
        ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE && (
        <FillInBlanksStudent
          {...(component as ComponentFillInBlanks)}
          isLoggedIn={isLoggedIn}
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_DRAW && (
        <DrawStudent
          {...(component as ComponentDraw)}
          isLoggedIn={isLoggedIn}
          {...openModalProps}
          onOpenDrawCanvas={() =>
            pushComponentViewEvent({
              component,
              eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
              expandMode: EVENT_ACTION_EDIT,
              eventTrackingData,
              contentId,
            })
          }
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE && (
        <DiscussionStudent
          {...(component as ComponentDiscussion)}
          isLoggedIn={false}
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE && (
        <InteractiveResponseStudent
          {...(component as ComponentInteractiveResponseSequence)}
          isLoggedIn={isLoggedIn}
        />
      )}

      {component.type ===
        ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE && (
        <InteractiveResponseCategoriseStudent
          {...(component as ComponentInteractiveResponseCategorise)}
          isLoggedIn={isLoggedIn}
        />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION && (
        <RiveView {...(component as ComponentRiveAnimation)} />
      )}

      {component.type === ACTIVITIES_COMPONENT_TYPE_GIF && (
        <GifView {...(component as ComponentGif)} {...openModalProps} />
      )}
    </>
  );
};

export default StudentComponents;
